/**
 * Yutrients chat widget — state machine + transport.
 *
 * Talks to the formulator via App Proxy (/apps/yutrients/chat/*).
 * Session id lives in sessionStorage (not a cookie) because Shopify's
 * App Proxy strips Set-Cookie response headers — see
 * docs/07-decisions/0019-storefront-shopify-theme.md § App Proxy
 * strips Set-Cookie.
 *
 * Full-page chat (28 Sep 2026): welcome screen → conversation, composer
 * pinned to the bottom of a fixed shell sized to the visual viewport
 * (see layoutShell). Markup: sections/yt-chat-widget.liquid.
 *
 * Adaptive funnel — soft target ~14 questions (13 answers, then the
 * build-or-continue choice); the customer can ask the AI questions
 * mid-flow or ask for more questions past 14. The server returns the same shape
 * either way (done:false {next question} or done:true {redirect});
 * this UI just renders whatever comes back.
 *
 * All customer-facing strings are read from data-* attributes on the
 * section element — set in sections/yt-chat-widget.liquid from
 * `section.settings`. A non-coder can change any of them via the
 * theme editor.
 *
 * Defensive against:
 *   - Network blips (retry button on error)
 *   - Rate limits (429 with Retry-After surfaces as a customer-facing
 *     "give it a moment" message; we don't auto-retry)
 *   - Customer double-tapping chips while a request is in flight
 *   - sessionStorage being absent (private mode iOS Safari) — in-memory
 *     fallback so the funnel still works for the current tab.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'yt_chat_session_id';

  /** sessionStorage with an in-memory fallback for private-mode browsers. */
  const store = (function () {
    let mem = null;
    let canUseSession = true;
    try {
      const probe = '__yt_probe__';
      window.sessionStorage.setItem(probe, '1');
      window.sessionStorage.removeItem(probe);
    } catch (_e) {
      canUseSession = false;
    }
    return {
      get() {
        if (canUseSession) {
          try {
            return window.sessionStorage.getItem(STORAGE_KEY) || null;
          } catch (_e) {
            return mem;
          }
        }
        return mem;
      },
      set(v) {
        mem = v;
        if (canUseSession) {
          try {
            window.sessionStorage.setItem(STORAGE_KEY, v);
          } catch (_e) {}
        }
      },
      clear() {
        mem = null;
        if (canUseSession) {
          try {
            window.sessionStorage.removeItem(STORAGE_KEY);
          } catch (_e) {}
        }
      },
    };
  })();

  /** Read a string from the section's data-* attribute, with a default. */
  function dataString(section, attr, fallback) {
    if (!section) return fallback;
    const v = section.getAttribute('data-yt-' + attr);
    return v == null || v === '' ? fallback : v;
  }

  /**
   * Attach the widget once the DOM has the root element. Multiple roots on
   * a page (unlikely) each get their own instance.
   */
  function init() {
    const roots = document.querySelectorAll('[data-yt-chat-root]');
    roots.forEach((root) => new ChatWidget(root));
  }

  class ChatWidget {
    constructor(root) {
      this.root = root;
      this.proxyBase = root.dataset.proxyBase || '/apps/yutrients';
      this.scroller = root.querySelector('[data-yt-chat-scroll]');
      this.transcript = root.querySelector('[data-yt-chat-transcript]');
      this.chipsRow = root.querySelector('[data-yt-chat-chips-row]');
      this.chips = root.querySelector('[data-yt-chat-chips]');
      this.composer = root.querySelector('[data-yt-chat-composer]');
      this.input = root.querySelector('[data-yt-chat-input]');
      this.send = root.querySelector('[data-yt-chat-send]');
      this.actions = root.querySelector('[data-yt-chat-actions]');
      this.restartBtn = root.querySelector('[data-yt-chat-restart]');
      this.formulateBtn = root.querySelector('[data-yt-chat-formulate]');
      this.errorEl = root.querySelector('[data-yt-chat-error]');
      this.progressEl = root.querySelector('[data-yt-chat-progress]');
      this.noteEl = root.querySelector('[data-yt-chat-note]');
      // The build overlay lives OUTSIDE the shell (sibling in the section)
      // and is re-parented to <body> when shown.
      this.formulaLoader = document.querySelector('[data-yt-chat-formula-loader]');

      // All editable strings live on the section element so the theme
      // editor can change them. Defaults match the schema.
      this.section = root.closest('[data-yt-chat-section]');
      this.copy = {
        markSrc: dataString(this.section, 'mark-src', ''),
        questionPrefix: dataString(this.section, 'question-prefix', 'Question'),
        longRouteSuffix: dataString(
          this.section,
          'long-route-suffix',
          '(taking the long route)',
        ),
        buildingMessage: dataString(
          this.section,
          'building-message',
          'Building your formula…',
        ),
        networkError: dataString(
          this.section,
          'network-error',
          'Network blip — please try again.',
        ),
        retryLabel: dataString(this.section, 'retry-label', 'Try again'),
        rateLimitPrefix: dataString(
          this.section,
          'rate-limit-prefix',
          "You're sending requests too quickly. Please give it about",
        ),
        welcomeChips: dataString(
          this.section,
          'welcome-chips',
          'Energy, Sleep, Sport, Longevity, Focus, Hormonal',
        ),
        consentPrefix: dataString(
          this.section,
          'consent-prefix',
          'Sending your first message means you accept',
        ),
        consentLink: dataString(this.section, 'consent-link', 'how the builder works'),
        disclaimerLine: dataString(
          this.section,
          'disclaimer-line',
          'A formulation tool, not medical advice.',
        ),
        disclaimerTitle: dataString(
          this.section,
          'disclaimer-title',
          'Before we start',
        ),
        disclaimerBody: dataString(
          this.section,
          'disclaimer-body',
          'The guided builder is a formulation tool, not a nutritionist or doctor — it can’t give medical advice, and it won’t ask about medical conditions or medication. Yutrients blends are food supplements, not medicines. If you’re pregnant, breastfeeding, taking medication or under medical care, speak to your doctor before starting anything new. By continuing, you agree to use the builder on this basis.',
        ),
        disclaimerDecline: dataString(
          this.section,
          'disclaimer-decline',
          'Rather choose ingredients yourself?',
        ),
        disclaimerDeclineUrl: dataString(
          this.section,
          'disclaimer-decline-url',
          '/pages/build-your-own',
        ),
      };

      this.busy = false;
      this.finished = false;
      // True once the customer has sent their first message — the welcome
      // screen gives way to the conversation.
      this.started = false;
      // The session's scripted first question (from POST /chat/session).
      this.firstQuestion = null;

      this.input.addEventListener('input', () => {
        this.updateSendState();
        this.autogrow();
      });
      // Enter sends, Shift+Enter makes a new line. While a reply is on its
      // way the textarea stays usable (so the mobile keyboard stays up and
      // the customer can draft), but nothing sends.
      this.input.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' && !ev.shiftKey && !ev.isComposing) {
          ev.preventDefault();
          const text = this.input.value.trim();
          if (text && !this.busy && !this.finished) this.submitAnswer(text);
        }
      });
      this.composer.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const text = this.input.value.trim();
        if (!text || this.busy || this.finished) return;
        this.submitAnswer(text);
      });
      // Keyboard opening shrinks the shell (layoutShell); keep the latest
      // message in view once it has settled.
      this.input.addEventListener('focus', () => {
        setTimeout(() => this.scrollToBottom(), 350);
      });
      // The send button must NOT steal focus — a blur closes the mobile
      // keyboard (founder 2026-08-13). pointerdown preventDefault keeps the
      // textarea focused; click still submits.
      this.send.addEventListener('pointerdown', (ev) => ev.preventDefault());

      // Keep the keyboard open (28 Sep 2026): a tap anywhere in the chat
      // that isn't a link, button or field puts the cursor back in the box.
      // A tap is a user gesture, so phones reopen the keyboard for it;
      // scrolling the messages doesn't fire a click and leaves it alone.
      this.root.addEventListener('click', (ev) => {
        if (this.finished) return;
        const t = ev.target;
        if (t.closest && t.closest('a, button, textarea, input, select, summary, label, [data-yt-chat-note]')) return;
        const sel = window.getSelection && window.getSelection();
        if (sel && String(sel).length) return;
        this.focusInput();
      });

      // Start over — wipe the session and start the funnel over.
      if (this.restartBtn) {
        this.restartBtn.addEventListener('click', () => this.restart());
      }
      // Formulate — force-finalise. HIDDEN until the customer has answered
      // every funnel question (soft_target - 1 answers), so nobody
      // formulates off one answer. The server enforces the same minimum on
      // /chat/formulate-now.
      this.formulateUnlocked = false;
      if (this.formulateBtn) {
        this.formulateBtn.hidden = true;
        this.formulateBtn.addEventListener('click', () => this.formulateNow());
      }

      this.bindViewport();
      this.autogrow();

      // The disclaimer gate (compliance — kept, restyled 28 Sep 2026). A
      // customer who has accepted before starts straight away, exactly as
      // before. A first-timer sees the welcome screen with an inline notice:
      // their first send IS the acceptance, and only then is a session
      // created (same as the old "I understand" button).
      this.consentNeeded = !this.disclaimerAccepted();
      this.renderNote();
      if (this.consentNeeded) {
        this.showStaticWelcome();
      } else {
        this.start({ focus: true });
      }
    }

    /* ── Disclaimer gate ────────────────────────────────────────── */

    static get DISCLAIMER_KEY() {
      return 'yt_chat_disclaimer_v1';
    }

    disclaimerAccepted() {
      try {
        return window.localStorage.getItem(ChatWidget.DISCLAIMER_KEY) != null;
      } catch (e) {
        // Storage blocked (private mode) — fall back to per-page memory.
        return this._disclaimerMem === true;
      }
    }

    /** Record acceptance exactly as the old gate's accept button did. */
    acceptDisclaimer() {
      if (window.ytTrack) ytTrack('chat_disclaimer', { choice: 'accepted' });
      try {
        window.localStorage.setItem(ChatWidget.DISCLAIMER_KEY, String(Date.now()));
      } catch (e) {
        this._disclaimerMem = true;
      }
      this.consentNeeded = false;
      this.renderNote();
    }

    /**
     * The line under the composer: the consent notice (with the full
     * disclaimer one tap away and the decline link) until the first send,
     * then the one-line "not medical advice" footnote.
     */
    renderNote() {
      if (!this.noteEl) return;
      this.noteEl.innerHTML = '';
      if (!this.consentNeeded) {
        const foot = document.createElement('p');
        foot.className = 'yt-chat-app__footnote';
        foot.textContent = this.copy.disclaimerLine;
        this.noteEl.appendChild(foot);
        return;
      }

      const line = document.createElement('p');
      line.className = 'yt-chat-app__consent';
      line.appendChild(document.createTextNode(this.copy.consentPrefix + ' '));

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'yt-chat-app__consent-toggle';
      toggle.textContent = this.copy.consentLink;
      toggle.setAttribute('aria-expanded', 'false');
      const panelId = 'yt-chat-disclaimer-' + Math.random().toString(36).slice(2, 8);
      toggle.setAttribute('aria-controls', panelId);
      // Keep the textarea focused (mobile keyboard) when tapping the link.
      toggle.addEventListener('pointerdown', (ev) => ev.preventDefault());
      line.appendChild(toggle);
      line.appendChild(document.createTextNode('. '));

      const decline = document.createElement('a');
      decline.className = 'yt-chat-app__consent-decline';
      decline.href = this.copy.disclaimerDeclineUrl;
      decline.textContent = this.copy.disclaimerDecline;
      // Analytics only — the anchor still navigates normally (gtag uses
      // sendBeacon, so the event survives the page unload).
      decline.addEventListener('click', () => {
        if (window.ytTrack) ytTrack('chat_disclaimer', { choice: 'declined' });
      });
      line.appendChild(decline);

      const panel = document.createElement('div');
      panel.className = 'yt-chat-app__disclaimer';
      panel.id = panelId;
      panel.hidden = true;
      const title = document.createElement('strong');
      title.textContent = this.copy.disclaimerTitle;
      const body = document.createElement('p');
      body.textContent = this.copy.disclaimerBody;
      panel.appendChild(title);
      panel.appendChild(body);

      toggle.addEventListener('click', () => {
        const open = panel.hidden;
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      this.noteEl.appendChild(panel);
      this.noteEl.appendChild(line);
    }

    /**
     * Welcome screen before consent: no session yet, so the suggestion
     * pills come from the section setting (kept in step with the
     * formulator's scripted opener chips).
     */
    showStaticWelcome() {
      this.clearLoading();
      const chips = String(this.copy.welcomeChips || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      this.renderChips(chips);
      this.focusInput();
    }

    /* ── Lifecycle ──────────────────────────────────────────────── */

    /**
     * Start a fresh session and render its scripted first question (into
     * the transcript, which stays hidden behind the welcome screen until
     * the first send). Resolves true on success.
     */
    async start(opts) {
      // Always a fresh session on page load — resuming needs a signed
      // Klaviyo token, which a fresh page-load doesn't have.
      store.clear();
      this.firstQuestion = null;
      try {
        const res = await this.fetchProxy('/chat/session', {
          method: 'POST',
          body: {},
        });
        if (!res.ok) {
          await this.handleErrorResponse(res, this.copy.networkError);
          return false;
        }
        const data = await res.json();
        store.set(data.session_id);
        this.clearLoading();
        this.firstQuestion = data.first_question || null;
        if (this.firstQuestion) this.renderAssistant(this.firstQuestion);
        if (opts && opts.focus) this.focusInput();
        return true;
      } catch (e) {
        this.clearLoading();
        this.showError(this.copy.networkError, () => this.start(opts));
        return false;
      }
    }

    async submitAnswer(answer) {
      if (this.busy || this.finished) return;
      // Typed formulate-intent (founder 2026-07-09): "formulate" — with
      // typos — typed as an answer acts exactly like the Formulate button.
      // Only once unlocked; before that it goes through as a normal answer
      // and the AI explains a few more questions are needed.
      if (this.isFormulateIntent(answer) && this.formulateUnlocked) {
        this.input.value = '';
        this.autogrow();
        this.renderUser(answer);
        this.formulateNow();
        return;
      }
      this.busy = true;
      this.hideError();
      this.input.value = '';
      this.autogrow();
      this.updateSendState();

      // The first send is the acceptance of the disclaimer (it replaced
      // the old modal gate), and a first-timer's session starts only now.
      if (this.consentNeeded) this.acceptDisclaimer();
      if (!store.get() || !this.firstQuestion) {
        this.composer.classList.add('is-waiting');
        const ok = await this.start();
        this.composer.classList.remove('is-waiting');
        if (!ok) {
          // Give the customer their words back so a retry is one tap.
          if (!this.input.value) this.input.value = answer;
          this.autogrow();
          this.busy = false;
          this.updateSendState();
          return;
        }
      }

      // Analytics: count answers, never their content. First real answer of
      // the page load also marks the session as started.
      this._answerCount = (this._answerCount || 0) + 1;
      if (window.ytTrack) {
        if (this._answerCount === 1) ytTrack('chat_started');
        ytTrack('chat_answer_sent', { answer_number: this._answerCount });
      }

      this.enterConversation();
      this.hideChips();
      this.renderUser(answer);
      // "Yutrients is typing" indicator until the server responds.
      this.renderTyping();
      this.composer.classList.add('is-waiting');

      try {
        const sessionId = store.get();
        if (!sessionId) {
          this.removeTyping();
          this.showError(this.copy.networkError, () => this.start());
          this.settle();
          return;
        }
        const res = await this.fetchProxy('/chat/turn', {
          method: 'POST',
          body: { session_id: sessionId, answer },
        });
        if (!res.ok) {
          this.removeTyping();
          // "Session has already finalised" (409) means a previous done-turn
          // or build attempt completed server-side even though the customer
          // saw an error — formulate-now returns their formula idempotently.
          let errCode = null;
          try { errCode = (await res.clone().json()).error && (await res.clone().json()).error.code; } catch (_e) {}
          if (errCode === 'conflict') {
            this.finished = true;
            this.showFormulaLoader();
            this.pollFormulateNow(sessionId);
            return;
          }
          await this.handleErrorResponse(res, this.copy.networkError);
          this.settle();
          return;
        }
        const data = await res.json();

        // Replace the typing indicator with the real response.
        this.removeTyping();

        if (data.done) {
          this.renderTransition(this.copy.buildingMessage);
          this.finished = true;
          this.input.disabled = true;
          this.updateSendState();
          if (data.building) {
            // Deferred finalise (2026-07-09): the done-turn returns
            // instantly and the build happens via formulate-now — which is
            // idempotent once the session finalises, so even if one attempt
            // gets killed by the App Proxy's ~28s ceiling, the next poll
            // returns the redirect.
            this.showFormulaLoader();
            this.pollFormulateNow(sessionId);
            return;
          }
          // Legacy server response (build ran in-band) — redirect directly.
          if (window.ytTrack && !this._trackedFormulaCreated) {
            this._trackedFormulaCreated = true;
            ytTrack('chat_formula_created');
          }
          setTimeout(() => {
            const redirect = data.redirect || '/pages/build-your-own';
            window.location.assign(redirect);
          }, 900);
          return;
        }

        this.renderAssistant({
          ...data.question,
          progress: data.progress,
        });
        this.settle();
      } catch (e) {
        this.removeTyping();
        // Give the customer a way back: retry re-sends the same answer so a
        // network blip mid-funnel doesn't dead-end them with no affordance.
        this.showError(this.copy.networkError, () => this.submitAnswer(answer));
        this.settle();
      }
    }

    /** A turn finished (answered or failed): the composer can send again. */
    settle() {
      this.busy = false;
      this.input.disabled = false;
      this.composer.classList.remove('is-waiting');
      this.updateSendState();
      // Back to the box after every reply, so the keyboard stays up.
      if (!this.finished) this.focusInput();
    }

    updateSendState() {
      this.send.disabled = !this.input.value.trim() || this.busy || this.finished;
    }

    focusInput() {
      // Desktop types immediately; mobile keyboards still need a tap.
      try { this.input.focus({ preventScroll: true }); } catch (_e) {}
    }

    /** Welcome screen → conversation, on the first send. */
    enterConversation() {
      if (this.started) return;
      this.started = true;
      this.root.classList.remove('is-welcome');
      this.root.classList.add('is-chatting');
    }

    /* ── Restart + Formulate ───────────────────────────────────── */

    /**
     * Wipe the session and reload — the simplest implementation, no
     * partial-state cleanup. Server-side the abandoned session is
     * TTL'd after 24h (see chat-page.md § State persistence) so we
     * don't need to explicitly tell the formulator the session is
     * dead; we just stop using it.
     */
    restart() {
      if (this.busy) return;
      // Analytics before the reload — gtag's sendBeacon survives it.
      if (window.ytTrack) ytTrack('chat_restarted');
      store.clear();
      window.location.reload();
    }

    /**
     * Force-finalise once unlocked (soft_target - 1 answers). Calls POST
     * /chat/formulate-now, which kicks the server into finaliseFlow (the
     * server re-checks the answer minimum and rejects earlier calls).
     * Server returns the same shape as a natural finalise (done:true +
     * formula_id + redirect), so the downstream handling is identical to
     * the "model decided we're done" branch in submitAnswer().
     */
    async formulateNow() {
      if (this.busy || this.finished) return;
      this.busy = true;
      this.input.disabled = true;
      this.send.disabled = true;
      if (this.restartBtn) this.restartBtn.disabled = true;
      if (this.formulateBtn) this.formulateBtn.disabled = true;
      this.disableChips();

      const sessionId = store.get();
      if (!sessionId) {
        this.showError(this.copy.networkError, () => this.start());
        this.busy = false;
        this.input.disabled = false;
        if (this.restartBtn) this.restartBtn.disabled = false;
        if (this.formulateBtn) this.formulateBtn.disabled = false;
        return;
      }
      this.showFormulaLoader();
      await this.pollFormulateNow(sessionId);
    }

    /* ── Formula loader (mirrors the BYO submit loader) ─────────── */

    showFormulaLoader() {
      if (!this.formulaLoader) {
        // Stale theme without the loader markup — fall back to the message.
        this.renderTransition(this.copy.buildingMessage);
        return;
      }
      // Full-viewport OVERLAY on top of the chat (2026-08-13). Re-parent to
      // <body> so no section's stacking context can paint over it.
      if (this.formulaLoader.parentElement !== document.body) {
        document.body.appendChild(this.formulaLoader);
      }
      this.formulaLoader.hidden = false;

      const host = this.formulaLoader.querySelector('[data-yt-submit-steps]');
      if (host && window.YtSubmitSteps) {
        // Shared 3-step loader, FIXED choreography (founder 2026-08-13:
        // same designed pacing for everyone; the backend runs in
        // parallel). The hold is the one reality gate — finish() releases
        // it when the blend is truly ready. Retune by editing this array.
        this.steps = new YtSubmitSteps(host, {
          titleEl: this.formulaLoader.querySelector('[data-yt-chat-loader-title]'),
          ariaLabel: 'Creating your formula',
        });
        this.steps.startTimeline({ durations: [15000, 17000, 10000], holdAt: 75 });
      } else {
        this.steps = null;
      }
    }

    hideFormulaLoader() {
      (this._loaderTimers || []).forEach(clearTimeout);
      this._loaderTimers = [];
      if (this.formulaLoader) this.formulaLoader.hidden = true;
      this.root.hidden = false;
    }

    finishFormulaLoader() {
      if (this.steps) this.steps.finish('Done — opening your formula…');
    }

    /* ── Rendering ──────────────────────────────────────────────── */

    clearLoading() {
      const loading = this.transcript.querySelector('[data-yt-chat-loading]');
      if (loading) loading.remove();
      this.transcript.setAttribute('aria-busy', 'false');
    }

    /** The small Yutrients mark beside every assistant message. */
    makeMark() {
      if (this.copy.markSrc) {
        const img = document.createElement('img');
        img.className = 'yt-chat-msg__mark';
        img.src = this.copy.markSrc;
        img.alt = '';
        img.width = 24;
        img.height = 24;
        img.setAttribute('aria-hidden', 'true');
        return img;
      }
      const span = document.createElement('span');
      span.className = 'yt-chat-msg__mark yt-chat-msg__mark--letter';
      span.textContent = 'Y';
      span.setAttribute('aria-hidden', 'true');
      return span;
    }

    /** One assistant row: mark + content, plain text on the canvas. */
    aiRow(content) {
      const wrap = document.createElement('div');
      wrap.className = 'yt-chat-msg yt-chat-msg--ai';
      wrap.appendChild(this.makeMark());
      const body = document.createElement('div');
      body.className = 'yt-chat-msg__text';
      if (typeof content === 'string') body.textContent = content;
      else if (content) body.appendChild(content);
      wrap.appendChild(body);
      return wrap;
    }

    renderAssistant(q) {
      // q: { text, options, freetext_only, progress: { turn, soft_target } }
      this.transcript.appendChild(this.aiRow(q.text));
      this.scrollToBottom();

      if (q.progress && q.progress.turn) {
        this.setProgress(q.progress);

        // Reveal Formulate on the ANSWER count, not the question number.
        // The displayed number drifts from the answer count whenever the
        // model answers a customer's own question (repeat turns), and the
        // server rejects formulate-now until soft_target - 1 answers exist,
        // so the two must key off the same count. The 14-question funnel
        // unlocks after 13 answers, when question 14 (the build-or-continue
        // choice) renders.
        const answersRequired = q.progress.soft_target
          ? q.progress.soft_target - 1
          : 13;
        if ((this._answerCount || 0) >= answersRequired) {
          this.formulateUnlocked = true;
          if (this.formulateBtn) this.formulateBtn.hidden = false;
        }
      }

      const opts =
        q.options && Array.isArray(q.options) && q.options.length > 0 && !q.freetext_only
          ? q.options
          : null;
      this.renderChips(opts);
    }

    /** Slim top-bar hint: "Question 4 of about 14". */
    setProgress(p) {
      if (!this.progressEl) return;
      let label = this.copy.questionPrefix + ' ' + p.turn;
      // "of about N" only while inside the soft target — the funnel is
      // adaptive and the customer can go past it; then the long-route
      // suffix replaces the denominator.
      if (p.soft_target && p.turn <= p.soft_target) {
        label += ' of about ' + p.soft_target;
      } else if (p.soft_target && p.turn > p.soft_target && this.copy.longRouteSuffix) {
        label += ' ' + this.copy.longRouteSuffix;
      }
      this.progressEl.textContent = label;
    }

    /** Suggestion pills above the composer (cap 6 — the goal opener has 6). */
    renderChips(options) {
      this.chips.innerHTML = '';
      if (!options || !options.length) {
        this.hideChips();
        return;
      }
      options.slice(0, 6).forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'yt-chat-chip';
        btn.textContent = opt;
        // Keep the textarea focused (mobile keyboard) when tapping a pill —
        // always, not only when it already had focus (28 Sep 2026: "the
        // keyboard should stay open at all times").
        btn.addEventListener('pointerdown', (ev) => ev.preventDefault());
        btn.addEventListener('click', () => {
          this.focusInput();
          // The exit question's "Build my blend now" chip acts as the
          // Formulate button (founder, 2026-07-23) — same build, no extra
          // round-trip through the model. Only once unlocked; before that
          // it's just an answer like any other.
          if (this.formulateUnlocked && /^build my blend/i.test(opt.trim())) {
            this.renderUser(opt);
            this.formulateNow();
            return;
          }
          this.submitAnswer(opt);
        });
        this.chips.appendChild(btn);
      });
      if (this.chipsRow) this.chipsRow.hidden = false;
    }

    hideChips() {
      if (this.chipsRow) this.chipsRow.hidden = true;
    }

    renderUser(text) {
      this.enterConversation();
      const wrap = document.createElement('div');
      wrap.className = 'yt-chat-msg yt-chat-msg--user';
      const bubble = document.createElement('div');
      bubble.className = 'yt-chat-msg__bubble';
      bubble.textContent = text;
      wrap.appendChild(bubble);
      this.transcript.appendChild(wrap);
      this.scrollToBottom();
    }

    /** Subtle "Yutrients is typing" row, replaced by the real reply. */
    renderTyping() {
      const dots = document.createElement('span');
      dots.className = 'yt-chat-typing';
      for (let i = 0; i < 3; i++) {
        const d = document.createElement('span');
        d.className = 'yt-chat-typing__dot';
        dots.appendChild(d);
      }
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = 'Yutrients is typing…';
      const holder = document.createElement('span');
      holder.appendChild(dots);
      holder.appendChild(sr);
      const wrap = this.aiRow(holder);
      wrap.setAttribute('data-yt-chat-typing', '');
      this.transcript.appendChild(wrap);
      this.scrollToBottom();
    }

    removeTyping() {
      const typing = this.transcript.querySelector('[data-yt-chat-typing]');
      if (typing) typing.remove();
    }

    renderTransition(text) {
      this.hideChips();
      this.chips.innerHTML = '';
      const holder = document.createElement('span');
      holder.className = 'yt-chat-msg__transition';
      const spinner = document.createElement('span');
      spinner.className = 'yt-chat-app__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      holder.appendChild(spinner);
      const t = document.createElement('span');
      t.textContent = text;
      holder.appendChild(t);
      this.transcript.appendChild(this.aiRow(holder));
      this.scrollToBottom();
    }

    disableChips() {
      this.chips.querySelectorAll('button').forEach((b) => (b.disabled = true));
    }

    scrollToBottom() {
      requestAnimationFrame(() => {
        if (this.scroller) this.scroller.scrollTop = this.scroller.scrollHeight;
      });
    }

    hideError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.innerHTML = '';
      }
    }

    /* ── Viewport ───────────────────────────────────────────────── */

    /**
     * The app shell is position:fixed under the site header and sized to
     * the VISUAL viewport, so on mobile the composer sits just above the
     * keyboard (iOS shrinks/pans the visual viewport, not the layout one).
     * Re-measured on every viewport change; the page itself never scrolls.
     */
    bindViewport() {
      if (!this.section) return;
      const schedule = () => {
        cancelAnimationFrame(this._layoutRaf);
        this._layoutRaf = requestAnimationFrame(() => this.layoutShell());
      };
      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('orientationchange', schedule);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', schedule);
        window.visualViewport.addEventListener('scroll', schedule);
      }
      if (window.ResizeObserver) {
        const ro = new ResizeObserver(schedule);
        document
          .querySelectorAll('.shopify-section-group-header-group')
          .forEach((el) => ro.observe(el));
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(schedule);
      }
      this.layoutShell();
    }

    layoutShell() {
      let headerBottom = 0;
      document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height > 0) headerBottom = Math.max(headerBottom, r.bottom);
      });
      const vv = window.visualViewport;
      const vvTop = vv ? vv.offsetTop : 0;
      const vvH = vv ? vv.height : window.innerHeight;
      const top = Math.max(0, headerBottom, vvTop);
      const height = Math.max(200, Math.round(vvTop + vvH - top));

      const s = this.scroller;
      const nearBottom = s ? s.scrollHeight - s.scrollTop - s.clientHeight < 96 : false;
      this.section.style.setProperty('--yt-chat-top', Math.round(top) + 'px');
      this.section.style.setProperty('--yt-chat-h', height + 'px');
      if (nearBottom) this.scrollToBottom();
    }

    /** Auto-size the textarea: 1 line at rest, grows to ~6 lines, then
     *  scrolls. Measured via a hidden MIRROR div — textarea scrollHeight
     *  proved unreliable inside flex contexts (phantom heights). */
    autogrow() {
      const el = this.input;
      if (!el || el.tagName !== 'TEXTAREA') return;
      if (!this._mirror) {
        const m = document.createElement('div');
        m.setAttribute('aria-hidden', 'true');
        m.style.cssText =
          'position:absolute;visibility:hidden;left:-9999px;top:0;' +
          'white-space:pre-wrap;word-wrap:break-word;box-sizing:border-box;';
        document.body.appendChild(m);
        this._mirror = m;
      }
      const cs = getComputedStyle(el);
      const m = this._mirror;
      m.style.width = el.clientWidth + 'px';
      // Individual font props — Firefox returns '' for the shorthand.
      m.style.fontFamily = cs.fontFamily;
      m.style.fontSize = cs.fontSize;
      m.style.fontWeight = cs.fontWeight;
      m.style.lineHeight = cs.lineHeight;
      m.style.padding = cs.padding;
      // Zero-width space keeps a trailing newline measurable.
      m.textContent = (el.value || 'x') + '​';
      const line = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5 || 24;
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const min = Math.ceil(line + padY);
      const max = Math.ceil(line * 6 + padY);
      const natural = Math.ceil(m.getBoundingClientRect().height);
      el.style.height = Math.max(min, Math.min(natural, max)) + 'px';
      el.style.overflowY = natural > max ? 'auto' : 'hidden';
    }

    /* ── Errors + transport ─────────────────────────────────────── */

    async handleErrorResponse(res, fallbackMsg) {
      let text = '';
      let body = null;
      try {
        text = await res.text();
        body = JSON.parse(text);
      } catch (_e) {}

      // Two throttles answer on this path: our own limiter (429 with a
      // JSON envelope) and Shopify's storefront throttle, which replies
      // FOR the App Proxy with a 430 / bare "local_rate_limited" body
      // when one IP sends too many requests. The Shopify one carries no
      // envelope, so without this check it fell through to the scary
      // "Network blip" message while retrying only dug the hole deeper.
      const throttled =
        res.status === 429 ||
        res.status === 430 ||
        /local_rate_limited/i.test(text || '');
      if (throttled) {
        const retryAfter =
          (body &&
            body.error &&
            body.error.details &&
            body.error.details.retry_after_seconds) ||
          parseInt(res.headers.get('retry-after') || '60', 10);
        const minutes = Math.max(1, Math.round(retryAfter / 60));
        const suffix =
          ' ' + minutes + ' minute' + (minutes === 1 ? '' : 's') + '.';
        this.showError(this.copy.rateLimitPrefix + suffix, null);
        return;
      }
      const detail =
        (body && body.error && body.error.message) ||
        fallbackMsg ||
        this.copy.networkError;
      this.showError(detail, () => this.start());
    }

    showError(message, retryFn) {
      this.errorEl.hidden = false;
      this.errorEl.innerHTML = '';
      const msg = document.createElement('span');
      msg.textContent = message;
      this.errorEl.appendChild(msg);
      if (retryFn) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = this.copy.retryLabel;
        btn.addEventListener('click', () => {
          this.errorEl.hidden = true;
          retryFn();
        });
        this.errorEl.appendChild(btn);
      }
    }

    /**
     * Typed formulate-intent: a short message whose token is "formulate"
     * within edit distance 2 (formualte, formulat, fomulate, formula…)
     * acts like pressing the Formulate button. Kept conservative: max 4
     * words so sentences that merely mention a formula don't trigger it.
     */
    isFormulateIntent(text) {
      const words = String(text || '').toLowerCase().trim().split(/[^a-z]+/).filter(Boolean);
      if (!words.length || words.length > 4) return false;
      const lev = (a, b) => {
        const m = a.length, n = b.length;
        const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
        for (let j = 1; j <= n; j++) d[0][j] = j;
        for (let i = 1; i <= m; i++)
          for (let j = 1; j <= n; j++)
            d[i][j] = Math.min(
              d[i - 1][j] + 1,
              d[i][j - 1] + 1,
              d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
            );
        return d[m][n];
      };
      return words.some((w) => w.length >= 6 && lev(w, 'formulate') <= 2);
    }

    /**
     * Drive the deferred finalise: call /chat/formulate-now until it hands
     * back the formula redirect. The first attempt usually starts (or joins)
     * the build server-side; if the App Proxy kills that request at ~28s,
     * the session still finalises in the background and the NEXT attempt
     * returns {done, redirect} instantly (the endpoint is idempotent once
     * finalised). Bounded attempts + a retry affordance if all fail.
     */
    async pollFormulateNow(sessionId) {
      // Budget, not attempt count. A finalise build is 1–2 minutes and the
      // App Proxy kills each request at ~28s, so what matters is how long
      // we're willing to keep asking — the old "5 attempts" gave up at
      // ~5.4 min of mostly-dead time and showed "Network blip" AFTER the
      // blend was ready (founder, 2026-07-27). Since migration 105 the
      // server answers 202 while a build is in flight, so a poll is cheap
      // and we can afford to wait the build out properly.
      const deadline = Date.now() + 240000; // 4 minutes
      const gapMs = 4000;
      while (Date.now() < deadline) {
        try {
          const res = await this.fetchProxy('/chat/formulate-now', {
            method: 'POST',
            body: { session_id: sessionId },
            // A build is a MUTATION. fetchProxy's blind retry is what
            // multiplied the builds — this loop owns the retries now.
            noRetry: true,
            // Just past the proxy's ~28s kill: waiting 75s for a request
            // the proxy already dropped only added dead time.
            timeoutMs: 32000,
          });
          // 202 = a build is already running (ours or a racing tab's).
          // Expected mid-build, not a failure — wait and ask again.
          if (res.status === 202) {
            await new Promise((r) => setTimeout(r, gapMs));
            continue;
          }
          if (res.ok) {
            const data = await res.json();
            if (data.done && (data.redirect || data.formula_id)) {
              this.finished = true;
              if (window.ytTrack && !this._trackedFormulaCreated) {
                this._trackedFormulaCreated = true;
                ytTrack('chat_formula_created');
              }
              // 28 Sep 2026 (Lucas): the AI recommends ONE ready-made base
              // and the customer goes straight to that base's product page,
              // carrying the formula (?blend=) so checkout reuses it. No
              // re-submit, no custom blend page.
              if (data.redirect && data.redirect.indexOf('/products/base-') === 0) {
                this.finishFormulaLoader();
                window.location.assign(data.redirect);
                return;
              }
              // Older sessions without a base: the previous direct route.
              if (data.formula_id) {
                const went = await this.directSubmit(data.formula_id);
                if (went) return;
              }
              // Fallback — the BYO review path, formula prefilled. Reached
              // only when the direct submit fails (server error, stale
              // theme assets); never a dead-end.
              this.finishFormulaLoader();
              // Founder 2026-08-27: finished blends land on the blend PDP,
              // not the customise page (whose ?stack= prefill predates base
              // formulas and dead-ends on them).
              const target = data.redirect ||
                '/products/personalised-blend?blend=' + encodeURIComponent(data.formula_id);
              setTimeout(() => window.location.assign(target), 1400);
              return;
            }
          }
        } catch (_e) {
          /* timed out / network — the build may still be running; poll again */
        }
        await new Promise((r) => setTimeout(r, gapMs));
      }
      // All attempts failed — bring the chat back so the error (with its
      // retry affordance) is actually visible, then retry re-opens the loader.
      this.hideFormulaLoader();
      this.busy = false;
      this.showError(this.copy.networkError, () => {
        this.showFormulaLoader();
        this.pollFormulateNow(sessionId);
      });
    }

    /**
     * Guided → PDP direct (founder 2026-08-13, supersedes the BYO stop
     * for chat customers): fetch the finalised formula, push it through
     * the SAME /formula/submit pipeline the BYO page uses — decision
     * 0020's single gatekeeper gains a second CALLER, not a second
     * pipeline; pricing, safety-flagging, the 90-day twin and
     * idempotency all ride along — then wait for the new variant to be
     * genuinely buyable and land the customer on their formula's PDP.
     * Returns false on ANY failure so the caller falls back to the BYO
     * review path (the formula row already exists; nothing is lost).
     */
    async directSubmit(formulaId) {
      try {
        const fRes = await this.fetchProxy('/formula/' + encodeURIComponent(formulaId), {
          timeoutMs: 20000,
        });
        if (!fRes.ok) return false;
        const f = await fRes.json();
        // BASED BLENDS (Lucas, 21 Sep 2026 — "fix this everywhere"). Every
        // chat blend is built on a base. GET /formula/:id returns the
        // COMBINED snapshot (the base recipe's own rows PLUS the add-ons),
        // because the leaflet and customer service show the whole blend —
        // but /formula/submit wants base_slug plus the ADD-ONS ONLY. Re-
        // posting the combined list with no base either 422s on the base's
        // below-menu doses (Daily Energy carries vitamin C at 80mg where the
        // component menu starts at 100mg) or, worse, quietly mints a
        // BASELESS duplicate: priced with the £15 dispensing line instead of
        // the base anchor, and sold as N component lines instead of the base
        // component.
        //
        // The split mirrors the server's own rule exactly (formulator
        // src/formula/pdp-payload.ts, isBaseSnapshotRow): an ADD-ON row
        // carries a numeric price share, a base row never does. A TOP-UP (a
        // second row for an ingredient the base already contains) carries a
        // share, so it stays in the submit.
        const baseSlug =
          typeof f.base_slug === 'string' && f.base_slug ? f.base_slug : null;
        const usable = (f.ingredients || []).filter(
          (i) => i && i.ingredient_id && typeof i.amount_mg === 'number',
        );
        // Lucas, 21 Sep 2026: the server now splits this for us (add_ons on GET /formula/:id). Prefer it: the share rule below cannot read a snapshot written by bundle intake, where the base and its add-ons are summed with no markers, and silently yields NOTHING for the 19 live blends in that shape. Fall back to the share rule only for a cached older payload.
        const served = Array.isArray(f.add_ons) ? f.add_ons : null;
        const servedIds = served && new Set(served.map((a) => a.ingredient_id));
        const ingredients = (
          baseSlug
            ? served
              ? usable.filter((i) => servedIds.has(i.ingredient_id))
              : usable.filter((i) => typeof i.price_share_gbp === 'number')
            : usable
        ).map((i) => ({
            ingredient_id: i.ingredient_id,
            amount_mg: i.amount_mg,
            // Keep the AI's per-ingredient blurbs — the submit creates a
            // NEW formula row from this payload, and the PDP renders these.
            rationale:
              typeof i.rationale === 'string' && i.rationale
                ? i.rationale.slice(0, 140)
                : undefined,
          }));
        // A based blend may legitimately have NO add-ons at all (the base on
        // its own is a complete blend); the server allows an empty add-on
        // list whenever base_slug is set. Without a base an empty list is
        // still nothing to submit.
        if (!ingredients.length && !baseSlug) return false;

        // Fresh Idempotency-Key per attempt (same rule as BYO): the server
        // only de-dupes a network double-fire of the SAME key.
        const idem = window.crypto && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
              const r = Math.random() * 16 | 0;
              return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
            });
        const res = await this.fetchProxy('/formula/submit', {
          method: 'POST',
          noRetry: true, // mutation — blind retries once multiplied builds
          timeoutMs: 32000,
          headers: { 'Idempotency-Key': idem },
          body: {
            stack: {
              stack_name: f.stack_name || 'My Blend',
              primary_goals: f.primary_goals || [],
              ingredients: ingredients,
              guided_overview: f.guided_overview || undefined,
            },
            // The base the blend was built on, so the new row is a based
            // blend too (21 Sep 2026): base anchor pricing, one base
            // component in the cart, no dispensing line.
            base_slug: baseSlug || undefined,
            formula_id_prefill: f.id,
            edited_after_ai: false,
          },
        });
        if (!res.ok) return false;
        const made = await res.json();
        if (window.ytTrack) {
          try {
            ytTrack('chat_submitted', {
              price: made.prices && made.prices.monthly_sub,
            });
          } catch (_e) { /* analytics must never break the flow */ }
        }

        // BUNDLE WORLD: nothing mints — the blend PDP hydrates itself from
        // the formulator via ?blend=<id> (same shareable mechanism as BYO),
        // so the chat needs NO payload plumbing. Instant, no variant poll.
        if (made.bundle || !made.variant_id) {
          if (!made.formula_id) return false;
          this.finishFormulaLoader();
          window.location.assign(
            '/products/personalised-blend?blend=' + encodeURIComponent(made.formula_id),
          );
          return true;
        }
        const numericVariantId = String(made.variant_id || '').replace(/\D/g, '');
        if (!numericVariantId) return false;

        if (window.ytPollVariantReady) {
          // Completes the moment the variant is buyable (replaces any
          // fixed soak); a timeout falls through — the PDP's own
          // not-ready handling backstops the rare index laggard. Capped
          // at 12s (founder 2026-08-13: long end-of-step waits read as
          // stalls; beyond ~12s the backstop is the better trade).
          await ytPollVariantReady('custom-blend', numericVariantId, { timeoutMs: 12000, gapMs: 2000 });
        }
        this.finishFormulaLoader();
        // The overlay STAYS UP through the navigation (same rule as BYO —
        // resetting early flashes the page back mid-commit). 1.4s lets
        // the finish glide (90 → 100) visibly complete first.
        setTimeout(() => {
          window.location.assign(
            '/products/custom-blend?variant=' + encodeURIComponent(numericVariantId),
          );
        }, 1400);
        return true;
      } catch (_e) {
        return false;
      }
    }

    /**
     * Proxy fetch with a request timeout + light retry.
     *
     * The funnel runs behind the Shopify App Proxy, which kills requests
     * that run too long; a slow finalise build used to surface to the
     * customer as a dead-end "network blip" after they'd answered every
     * question. We now (a) bound each request with an AbortController so
     * it fails predictably rather than hanging, and (b) retry once on a
     * transient network error / 5xx / timeout. Since 2026-07-09 the
     * done-turn itself returns instantly (deferred finalise) — the slow
     * build lives in pollFormulateNow above, which tolerates proxy kills.
     */
    async fetchProxy(path, { method, body, noRetry, timeoutMs: timeoutOverride, headers } = {}) {
      // Callers that own their own retry loop pass noRetry — retrying a
      // MUTATING call blindly is what let one customer kick off ten
      // concurrent formula builds (see pollFormulateNow).
      const timeoutMs = timeoutOverride || 75000; // generous: covers the done-turn build
      const attempts = noRetry ? 1 : 2;
      let lastErr = null;
      for (let i = 0; i < attempts; i++) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), timeoutMs);
        try {
          const res = await fetch(this.proxyBase + path, {
            method: method || 'GET',
            credentials: 'same-origin',
            headers: Object.assign(
              { 'Content-Type': 'application/json', Accept: 'application/json' },
              headers || {},
            ),
            body: body ? JSON.stringify(body) : undefined,
            signal: ctrl.signal,
          });
          clearTimeout(timer);
          // Retry once on a transient upstream 5xx, and on Shopify's
          // per-IP storefront throttle (429/430) after a longer pause —
          // its window is short, so one spaced retry usually clears it.
          if (res.status >= 500 && i < attempts - 1) {
            await new Promise((r) => setTimeout(r, 1200));
            continue;
          }
          if ((res.status === 429 || res.status === 430) && i < attempts - 1) {
            await new Promise((r) => setTimeout(r, 2500));
            continue;
          }
          return res;
        } catch (e) {
          clearTimeout(timer);
          lastErr = e;
          if (i < attempts - 1) {
            await new Promise((r) => setTimeout(r, 1200));
            continue;
          }
        }
      }
      throw lastErr || new Error('network');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // bfcache restore: hitting Back after the redirect (or navigating away
  // mid-flow) can restore a frozen page — stale "Building your formula…"
  // transition, disabled composer, spinner still up. Reload so the
  // customer lands on a fresh, interactive chat instead of a dead one.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) window.location.reload();
  });
})();
